/**
 * @param {HTMLElement} root
 */
export default function initDomainePouchMailBackForm(root) {
  const form = root.querySelector('form');
  const messageEl = root.querySelector('[data-shippo-message]');
  const submitButton = form?.querySelector('[type="submit"]');
  const url = form?.getAttribute('action');
  const translations = /** @type {typeof window & { pouchMailBackFormTranslations?: Record<string, string> }} */ (
    window
  ).pouchMailBackFormTranslations;

  if (!(form instanceof HTMLFormElement) || !url) return;

  populateStateSelect(form);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    setFormMessage(messageEl instanceof HTMLElement ? messageEl : null, '', true);

    if (!form.reportValidity()) {
      return;
    }

    setLoading(submitButton instanceof HTMLButtonElement ? submitButton : null, true, form);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildQuery(form)),
        mode: 'cors',
      });

      const data = await response.json();
      handleShippoResponse(data, messageEl instanceof HTMLElement ? messageEl : null, translations);
    } catch {
      setFormMessage(messageEl instanceof HTMLElement ? messageEl : null, translations?.error || 'Error', true);
    } finally {
      setLoading(submitButton instanceof HTMLButtonElement ? submitButton : null, false, form);
    }
  });
}

/**
 * @param {HTMLFormElement} form
 */
function buildQuery(form) {
  const formData = new FormData(form);
  /** @type {Record<string, string>} */
  const query = { country: 'US' };

  for (const [key, value] of formData.entries()) {
    if (typeof value === 'string') {
      query[key] = value;
    }
  }

  return query;
}

/**
 * @param {HTMLFormElement} form
 */
function populateStateSelect(form) {
  const select = form.querySelector('[name="state"]');

  if (!(select instanceof HTMLSelectElement)) return;

  const countries = /** @type {typeof window & { Countries?: Record<string, { provinces?: string[][] }> }} */ (
    window
  ).Countries;
  const provinces =
    countries?.['United States']?.provinces ?? [];

  for (const province of provinces) {
    const provinceString = province.toString()
    if (province.length === 0) continue;

    const option = document.createElement('option');
    option.value = provinceString;
    option.textContent = provinceString;
    select.appendChild(option);
  }
}

/**
 * @param {unknown} response
 * @param {HTMLElement | null} messageEl
 * @param {Record<string, string> | undefined} translations
 */
function handleShippoResponse(response, messageEl, translations) {
  if (
    response &&
    typeof response === 'object' &&
    'label_url' in response &&
    'status' in response &&
    response.label_url &&
    response.status === 'SUCCESS'
  ) {
    window.open(String(response.label_url), '_blank');
    setFormMessage(messageEl, translations?.submitSuccess ?? response.status, false);
    return;
  } else if (
    response &&
    typeof response === 'object' &&
    'status' in response &&
    response.status === 'ERROR' &&
    'messages' in response &&
    Array.isArray(response.messages) &&
    response.messages[0]?.text
  ) {
    const messageText = String(response.messages[0].text);

    if (messageText.includes('originZIPCode')) {
      setFormMessage(messageEl, translations?.zipError ?? messageText, true);
    } else {
      setFormMessage(messageEl, messageText, true);
    }
    return;
  } else {
    setFormMessage(messageEl instanceof HTMLElement ? messageEl : null, translations?.error || 'Error', true);
  }

  const shipment =
    response &&
    typeof response === 'object' &&
    'shipment' in response &&
    Array.isArray(response.shipment)
      ? response.shipment[0]
      : undefined;

  const addressFromError = shipment?.address_from?.[0]?.__all__?.[0];
  const addressToError = shipment?.address_to?.[0]?.__all__?.[0];

  setFormMessage(messageEl, addressFromError || addressToError || '', true);
}

/**
 * @param {HTMLElement | null} messageEl
 * @param {string} message
 * @param {boolean} isError
 */
function setFormMessage(messageEl, message, isError) {
  if (!messageEl) return;

  messageEl.textContent = message;
  if (isError) {
    messageEl.classList.remove("success")
    messageEl.classList.add("error")
  } else {
    messageEl.classList.remove("error")
    messageEl.classList.add("success")
  }
  messageEl.hidden = !message;
}

/**
 * @param {HTMLButtonElement | null} submitButton
 * @param {boolean} isLoading
 * @param {HTMLFormElement | null} form
 */
function setLoading(submitButton, isLoading, form) {
  if (!submitButton) return;

  submitButton.disabled = isLoading;
  form?.classList.toggle('is-loading', isLoading)
}

for (const root of document.querySelectorAll('[data-module="pouch-mail-back-form"]')) {
  if (root instanceof HTMLElement) {
    initDomainePouchMailBackForm(root);
  }
}
